"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { toIntlLocale } from "@/lib/locale";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { PickupDateCalendar } from "@/components/ui/pickup-date-calendar";
import {
  getCakeDateAvailabilityStateForCart,
  hasBentoCakeSize,
  hasRegularCakeSize,
  type PickupAvailabilityRecord,
} from "@/lib/pickupAvailability";

type DateStatus = "available" | "limited" | "full" | "unavailable" | "closed";

export type PickupDateRailLabels = {
  moreDates: string;
  today: string;
  tomorrow: string;
  limitedBadge: string;
  fullBadge: string;
  closedBadge: string;
  scrollPrevious: string;
  scrollNext: string;
  noUpcomingDates: string;
  pickDate: string;
  legendTitle?: string;
  legendAvailable?: string;
  legendLimited?: string;
  legendFullyBooked?: string;
  legendPastOrNotScheduled?: string;
};

export interface PickupDateRailProps {
  selectedDate?: string | null;
  onDateSelect: (isoDate: string) => void;
  availabilityRecords: PickupAvailabilityRecord[];
  orderType?: "cake" | "cake-and-cupcakes" | "cupcakes" | "merch";
  cakeServings?: string;
  cakeAllowedServings?: readonly string[];
  /**
   * Effective bento mapping from the admin order-options: the set of serving
   * VALUES that count toward the BENTO capacity bucket. When provided, the
   * bento/regular split for date selectability follows this set (so a custom
   * non-`1-4` range marked bento is judged against the bento counters); when
   * omitted, classification falls back to the legacy `value === "1-4"` rule.
   * See `hasBentoCakeSize` in lib/pickupAvailability.ts.
   */
  bentoServings?: ReadonlySet<string>;
  /**
   * Cart-aware capacity filter — bento (`1-4` servings) cakes required
   * across the whole cart. When >0, dates whose remaining bento capacity
   * is insufficient are marked `"full"` so the customer can't pick a
   * date the server will reject at submit. Defaults to 0 (no filter).
   */
  requiredBentoSlots?: number;
  /** Same as `requiredBentoSlots` but for the regular-cake bucket. */
  requiredRegularSlots?: number;
  /**
   * Additive merch pickup-availability constraint (Phase C1,
   * `docs/merch-pickup-availability-plan.md` §4). When non-null, a date is only
   * selectable if it is ALSO present in this set of allowed ISO dates (the merch
   * per-product intersection for the currently-chosen location). `null`/omitted
   * means NO merch constraint — cake-only / cupcake carts are byte-identical.
   * This is purely additive: a date must satisfy the cake rules where applicable
   * AND be in this set.
   */
  merchAllowedDates?: ReadonlySet<string> | null;
  minDate?: string;
  daysToShow?: number;
  locale?: string;
  labels: PickupDateRailLabels;
  className?: string;
  /**
   * Optional label rendered inline with the "More dates" action, so the rail
   * header forms a single row (e.g. "Pickup date         More dates ▸").
   */
  headerLabel?: ReactNode;
}

function dateToISO(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function dateFromISO(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

function computeStatus(
  record: PickupAvailabilityRecord | undefined,
  orderType: PickupDateRailProps["orderType"],
  cakeServings: string | undefined,
  cakeAllowedServings: readonly string[] | undefined,
  bentoServings: ReadonlySet<string> | undefined,
  requiredBentoSlots: number,
  requiredRegularSlots: number,
  merchAllowedDates: ReadonlySet<string> | null | undefined,
): DateStatus {
  if (!record) return "closed";

  // Additive merch constraint (Phase C1): when a merch intersection is supplied,
  // a date the merch products can't fulfil is CLOSED regardless of the cake
  // calendar. Applied first so it gates both merch-only and mixed carts. `null`
  // / undefined → no constraint (cake-only byte-identical).
  if (merchAllowedDates && !merchAllowedDates.has(record.date)) {
    return "closed";
  }

  // Merch-only carts consume ZERO cake capacity: any OPEN slot is selectable
  // regardless of the day's cake counters (which can legitimately be 0 for a
  // non-cake collection slot). Decided BEFORE the cake `status === "full"`
  // short-circuit because that "full" flag reflects cake capacity, which does
  // not constrain merch. `record.status === "full"`/`"notAvailable"`/anything
  // other than `"open"` means the slot is not a valid pickup window, so it
  // stays closed.
  if (orderType === "merch") {
    return record.status === "open" ? "available" : "closed";
  }

  if (record.status === "full") return "full";
  if (record.status === "notAvailable") return "unavailable";
  if (record.status !== "open") return "closed";

  const hasCake = orderType === "cake" || orderType === "cake-and-cupcakes";

  const regularRemaining = Math.max(
    0,
    record.maxOrders - record.confirmedOrders,
  );
  const bentoRemaining = Math.max(
    0,
    record.maxBentoOrders - record.confirmedBentoOrders,
  );
  // Cart-aware "full" check: the slot has *some* capacity but not enough for
  // what the customer's cart actually needs. Falls back to the existing
  // ">= max" check when no required count is provided (legacy callers).
  const regularFull =
    record.maxOrders > 0 &&
    (record.confirmedOrders >= record.maxOrders ||
      (requiredRegularSlots > 0 && regularRemaining < requiredRegularSlots));
  const bentoFull =
    record.maxBentoOrders > 0 &&
    (record.confirmedBentoOrders >= record.maxBentoOrders ||
      (requiredBentoSlots > 0 && bentoRemaining < requiredBentoSlots));
  const regularLimited = record.maxOrders > 0 && regularRemaining === 1;
  const bentoLimited = record.maxBentoOrders > 0 && bentoRemaining === 1;

  if (!hasCake) {
    if (regularFull) return "full";
    if (regularLimited) return "limited";
    return record.maxOrders > 0 ? "available" : "closed";
  }

  if (cakeAllowedServings && cakeAllowedServings.length > 0) {
    return getCakeDateAvailabilityStateForCart(record, {
      hasBentoOption: hasBentoCakeSize(cakeAllowedServings, bentoServings),
      hasRegularOption: hasRegularCakeSize(cakeAllowedServings, bentoServings),
      requiredBentoSlots,
      requiredRegularSlots,
    }).status;
  }

  // Effective bento classification for the single selected serving (no
  // allow-list path). Falls back to the legacy `=== "1-4"` rule when no
  // `bentoServings` set is supplied.
  const isBentoOrder = bentoServings
    ? cakeServings !== undefined && bentoServings.has(cakeServings)
    : cakeServings === "1-4";

  if (isBentoOrder) {
    if (bentoFull) return "full";
    if (bentoLimited) return "limited";
    return record.maxBentoOrders > 0 ? "available" : "closed";
  }

  if (regularFull) return "full";
  if (regularLimited) return "limited";
  return record.maxOrders > 0 ? "available" : "closed";
}

interface RailEntry {
  iso: string;
  status: DateStatus;
  isToday: boolean;
  isTomorrow: boolean;
  weekdayLabel: string;
  dayNumber: string;
  monthLabel: string;
}

/**
 * Horizontal, branded pickup-date rail with availability-aware chips and a
 * "More dates" popover escape-hatch that opens the existing calendar. Reusable
 * across the product page, order form flow, and reschedule dialogs.
 */
export function PickupDateRail({
  selectedDate,
  onDateSelect,
  availabilityRecords,
  orderType,
  cakeServings,
  cakeAllowedServings,
  bentoServings,
  requiredBentoSlots = 0,
  requiredRegularSlots = 0,
  merchAllowedDates = null,
  minDate,
  daysToShow = 14,
  locale = "en",
  labels,
  className,
  headerLabel,
}: PickupDateRailProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const minIso = minDate || dateToISO(new Date());
  const minDateObj = useMemo(() => dateFromISO(minIso), [minIso]);
  // Recomputed per render so "today"/"tomorrow" badges advance if the
  // component stays mounted across midnight.
  const now = new Date();
  const todayIso = dateToISO(now);
  const tomorrowIso = dateToISO(addDays(now, 1));

  const recordByIso = useMemo(() => {
    const map = new Map<string, PickupAvailabilityRecord>();
    for (const record of availabilityRecords) {
      map.set(record.date, record);
    }
    return map;
  }, [availabilityRecords]);

  const entries = useMemo<RailEntry[]>(() => {
    const intlLocale = toIntlLocale(locale);
    const weekdayFmt = new Intl.DateTimeFormat(intlLocale, {
      weekday: "short",
    });
    const monthFmt = new Intl.DateTimeFormat(intlLocale, { month: "short" });

    // Iterate at least `daysToShow` consecutive days from today, but extend
    // the window to cover the latest scheduled record so sparse availability
    // (e.g. a single Vlaardingen slot 5+ weeks out) still appears in the rail.
    // Otherwise the calendar popover would show the date but the rail would
    // not — confusing users who can never click the chip directly.
    let latestRecordedIso: string | undefined;
    for (const record of availabilityRecords) {
      if (record.date < minIso) continue;
      if (!latestRecordedIso || record.date > latestRecordedIso) {
        latestRecordedIso = record.date;
      }
    }

    const baseLastIso = dateToISO(addDays(minDateObj, daysToShow - 1));
    const lastIso =
      latestRecordedIso && latestRecordedIso > baseLastIso
        ? latestRecordedIso
        : baseLastIso;
    // Compute the total day count via UTC noon timestamps so DST transitions
    // (Europe/Amsterdam shifts an hour twice a year) don't shave or add a day
    // when the last record sits across a transition from today.
    const minUtcNoon = Date.UTC(
      minDateObj.getFullYear(),
      minDateObj.getMonth(),
      minDateObj.getDate(),
      12,
    );
    const [lastY, lastM, lastD] = lastIso.split("-").map(Number);
    const lastUtcNoon = Date.UTC(lastY, (lastM ?? 1) - 1, lastD ?? 1, 12);
    const millisPerDay = 24 * 60 * 60 * 1000;
    const totalDays =
      Math.max(0, Math.round((lastUtcNoon - minUtcNoon) / millisPerDay)) + 1;

    const list: RailEntry[] = [];
    for (let i = 0; i < totalDays; i++) {
      const date = addDays(minDateObj, i);
      const iso = dateToISO(date);
      list.push({
        iso,
        status: computeStatus(
          recordByIso.get(iso),
          orderType,
          cakeServings,
          cakeAllowedServings,
          bentoServings,
          requiredBentoSlots,
          requiredRegularSlots,
          merchAllowedDates,
        ),
        isToday: iso === todayIso,
        isTomorrow: iso === tomorrowIso,
        weekdayLabel: weekdayFmt.format(date),
        dayNumber: String(date.getDate()),
        monthLabel: monthFmt.format(date),
      });
    }
    return list;
  }, [
    minDateObj,
    minIso,
    daysToShow,
    recordByIso,
    availabilityRecords,
    orderType,
    cakeServings,
    cakeAllowedServings,
    bentoServings,
    requiredBentoSlots,
    requiredRegularSlots,
    merchAllowedDates,
    todayIso,
    tomorrowIso,
    locale,
  ]);

  const visibleEntries = useMemo(
    () => entries.filter((entry) => entry.status !== "closed"),
    [entries],
  );

  const scrollBy = useCallback((direction: 1 | -1) => {
    const el = scrollerRef.current;
    if (!el) return;
    const firstChip = el.querySelector<HTMLElement>("[data-rail-chip]");
    const chipWidth = firstChip ? firstChip.offsetWidth + 10 : 96;
    el.scrollBy({ left: direction * chipWidth * 3, behavior: "smooth" });
  }, []);

  const updateArrowState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const scrollItems = el.querySelectorAll<HTMLElement>("[data-rail-scroll-item]");
    const firstItem = scrollItems[0];
    const lastItem = scrollItems[scrollItems.length - 1];

    if (!firstItem || !lastItem) {
      setCanScrollLeft(false);
      setCanScrollRight(false);
      return;
    }

    const viewportStart = el.scrollLeft;
    const viewportEnd = el.scrollLeft + el.clientWidth;
    const firstItemStart = firstItem.offsetLeft;
    const lastItemEnd = lastItem.offsetLeft + lastItem.offsetWidth;
    const hasOverflow = lastItemEnd - firstItemStart > el.clientWidth + 4;
    const nextCanScrollLeft = hasOverflow && viewportStart > firstItemStart + 4;
    const nextCanScrollRight = hasOverflow && viewportEnd < lastItemEnd - 4;

    setCanScrollLeft(nextCanScrollLeft);
    setCanScrollRight(nextCanScrollRight);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const syncArrows = () => updateArrowState();
    syncArrows();

    el.addEventListener("scroll", syncArrows, { passive: true });
    window.addEventListener("resize", syncArrows);

    let frameId = window.requestAnimationFrame(syncArrows);
    let resizeObserver: ResizeObserver | null = null;

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(syncArrows);
      resizeObserver.observe(el);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      el.removeEventListener("scroll", syncArrows);
      window.removeEventListener("resize", syncArrows);
      resizeObserver?.disconnect();
    };
  }, [updateArrowState, visibleEntries.length]);

  useEffect(() => {
    if (!selectedDate) return;
    const el = scrollerRef.current;
    if (!el) return;
    const target = el.querySelector<HTMLElement>(
      `[data-iso="${selectedDate}"]`,
    );
    if (!target) return;
    // Scroll only the rail container horizontally. Using `scrollIntoView`
    // with `block: "nearest"` also scrolls the document vertically when the
    // rail is below the fold (e.g. auto-selecting the first available date
    // on mount for tall product forms like gender-reveal cakes), which
    // makes the page appear to jump to the form on load.
    const targetCenter = target.offsetLeft + target.offsetWidth / 2;
    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    const nextScrollLeft = Math.max(
      0,
      Math.min(maxScrollLeft, targetCenter - el.clientWidth / 2),
    );
    if (typeof el.scrollTo === "function") {
      el.scrollTo({ left: nextScrollLeft, behavior: "smooth" });
    } else {
      el.scrollLeft = nextScrollLeft;
    }
    const frameId = window.requestAnimationFrame(updateArrowState);
    return () => window.cancelAnimationFrame(frameId);
  }, [selectedDate, updateArrowState]);

  const handleKeyDown = (
    e: KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const el = scrollerRef.current;
    if (!el) return;
    const direction = e.key === "ArrowRight" ? 1 : -1;
    const nextIndex = Math.max(
      0,
      Math.min(visibleEntries.length - 1, index + direction),
    );
    const buttons = el.querySelectorAll<HTMLButtonElement>("[data-rail-chip]");
    buttons[nextIndex]?.focus();
  };

  const firstSelectableIndex = visibleEntries.findIndex(
    (entry) => entry.status === "available" || entry.status === "limited",
  );

  const hasAvailabilityData = availabilityRecords.length > 0;

  return (
    <div className={cn("w-full space-y-2", className)}>
      {/* Header row: optional label + "More dates" escape hatch */}
      <div
        className={cn(
          "flex items-center gap-2",
          headerLabel ? "justify-between" : "justify-end",
        )}
      >
        {headerLabel && <div className="min-w-0">{headerLabel}</div>}
        {hasAvailabilityData && (
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 rounded-full border-primary/30 text-primary hover:border-primary/60 hover:bg-primary/5 hover:text-primary"
              >
                <CalendarDays className="h-3.5 w-3.5" />
                {labels.moreDates}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              sideOffset={8}
              className="w-auto border-border p-2 shadow-lg"
            >
              <PickupDateCalendar
                mode="single"
                selectedDate={selectedDate || null}
                onDateSelect={(iso) => {
                  if (!iso) return;
                  onDateSelect(iso);
                  setIsCalendarOpen(false);
                }}
                availabilityRecords={availabilityRecords}
                orderType={orderType}
                cakeServings={cakeServings}
                cakeAllowedServings={cakeAllowedServings}
                bentoServings={bentoServings}
                requiredBentoSlots={requiredBentoSlots}
                requiredRegularSlots={requiredRegularSlots}
                merchAllowedDates={merchAllowedDates}
                minDate={minIso}
                showLegend
                legendLabels={{
                  title: labels.legendTitle,
                  available: labels.legendAvailable,
                  limited: labels.legendLimited,
                  fullyBooked: labels.legendFullyBooked,
                  pastOrNotScheduled: labels.legendPastOrNotScheduled,
                }}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>

      {visibleEntries.length > 0 && <div className="relative">
        {canScrollLeft && (
          <button
            type="button"
            data-rail-arrow-prev
            onClick={() => scrollBy(-1)}
            aria-label={labels.scrollPrevious}
            className="absolute left-1 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/95 shadow-sm backdrop-blur transition-colors hover:border-primary/50 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:h-9 sm:w-9"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
        )}

        <div
          ref={scrollerRef}
          data-rail-scroller
          className="flex snap-x snap-mandatory gap-2.5 overflow-x-auto scroll-smooth px-10 py-3 sm:px-12 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <div role="radiogroup" aria-label={labels.pickDate} className="contents">
            {visibleEntries.map((entry, index) => {
              const isSelected = selectedDate === entry.iso;
              const isDisabled =
                entry.status === "full" ||
                entry.status === "unavailable" ||
                entry.status === "closed";
              const isFocusable = isSelected
                ? true
                : !selectedDate && index === Math.max(0, firstSelectableIndex);

              return (
                <button
                  key={entry.iso}
                  type="button"
                  data-rail-scroll-item
                  data-rail-chip
                  data-rail-index={index}
                  data-iso={entry.iso}
                  role="radio"
                  aria-checked={isSelected}
                  aria-disabled={isDisabled}
                  disabled={isDisabled}
                  tabIndex={isFocusable ? 0 : -1}
                  onClick={() => {
                    if (isDisabled) return;
                    onDateSelect(entry.iso);
                  }}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className={cn(
                    "group relative flex min-w-[78px] snap-start flex-col items-center justify-start gap-0.5 rounded-xl border-2 px-3 pb-2 pt-3 transition-all duration-200",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
                    isSelected &&
                      "animate-fade-in-up border-primary bg-primary text-primary-foreground shadow-md",
                    !isSelected &&
                      entry.status === "available" &&
                      "border-primary/40 bg-primary/5 text-primary hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10 hover:shadow-sm",
                    !isSelected &&
                      entry.status === "limited" &&
                      "border-amber-500/50 bg-amber-50 text-amber-800 hover:-translate-y-0.5 hover:border-amber-500 hover:bg-amber-100 hover:shadow-sm",
                    !isSelected &&
                      entry.status === "full" &&
                      "cursor-not-allowed border-border bg-muted/50 text-muted-foreground",
                    !isSelected &&
                      entry.status === "unavailable" &&
                      "cursor-not-allowed border-dashed border-border bg-background text-muted-foreground/70",
                    !isSelected &&
                      entry.status === "closed" &&
                      "cursor-not-allowed border-dashed border-border bg-background text-muted-foreground/70",
                  )}
                >
                  {(entry.isToday || entry.isTomorrow) && (
                    <span
                      className={cn(
                        "absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide",
                        isSelected
                          ? "bg-white text-primary"
                          : entry.status === "available" ||
                              entry.status === "limited"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground",
                      )}
                    >
                      {entry.isToday ? labels.today : labels.tomorrow}
                    </span>
                  )}

                  <span
                    className={cn(
                      "text-[10px] font-semibold uppercase tracking-wide",
                      isSelected ? "text-primary-foreground/80" : "opacity-70",
                    )}
                  >
                    {entry.weekdayLabel}
                  </span>
                  <span
                    className={cn(
                      "text-2xl font-bold leading-none",
                      entry.status === "full" && !isSelected && "line-through",
                    )}
                  >
                    {entry.dayNumber}
                  </span>
                  <span
                    className={cn(
                      "text-[10px] font-medium uppercase tracking-wide",
                      isSelected ? "text-primary-foreground/80" : "opacity-70",
                    )}
                  >
                    {entry.monthLabel}
                  </span>

                  {entry.status === "limited" && (
                    <span
                      className={cn(
                        "mt-1 rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide",
                        isSelected
                          ? "bg-white/95 text-primary shadow-sm"
                          : "bg-amber-500/20 text-amber-800",
                      )}
                    >
                      {labels.limitedBadge}
                    </span>
                  )}
                  {entry.status === "full" && !isSelected && (
                    <span className="mt-1 rounded-full bg-destructive/10 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-destructive">
                      {labels.fullBadge}
                    </span>
                  )}
                  {entry.status === "unavailable" && !isSelected && (
                    <span className="mt-1 rounded-full bg-muted px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {labels.closedBadge}
                    </span>
                  )}
                  {entry.status === "closed" && !isSelected && (
                    <span className="mt-1 rounded-full bg-muted px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {labels.closedBadge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {hasAvailabilityData && visibleEntries.length > 0 && (
            <button
              type="button"
              data-rail-scroll-item
              data-rail-more
              data-rail-index={visibleEntries.length}
              onClick={() => setIsCalendarOpen(true)}
              className="group flex min-w-[98px] snap-start flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-primary/35 bg-gradient-to-br from-primary/10 via-background to-background px-3 py-3 text-primary transition-all duration-200 hover:-translate-y-0.5 hover:border-primary/60 hover:bg-primary/5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <CalendarDays className="h-4 w-4" />
              <span className="text-[10px] font-semibold uppercase tracking-wide opacity-80">
                {labels.moreDates}
              </span>
              <ChevronRight className="h-3.5 w-3.5 opacity-70 transition-transform group-hover:translate-x-0.5" />
            </button>
          )}
        </div>

        {canScrollRight && (
          <button
            type="button"
            data-rail-arrow-next
            onClick={() => scrollBy(1)}
            aria-label={labels.scrollNext}
            className="absolute right-1 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/95 shadow-sm backdrop-blur transition-colors hover:border-primary/50 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:h-9 sm:w-9"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>}

    </div>
  );
}
